// ============================================================================
// IBM MAXIMO ASSET MANAGEMENT (CMMS) CONNECTOR ENGINE
// Optimal® Availability Studio — Maximo Portal Integration
// Supports: Assets, Commodities, Crafts, Items/Spare Parts, Job Plans,
//           Locations, Planned Maintenance, Work Orders, and Performance KPIs
// Formats:  REST API, OSLC (Open Services for Lifecycle Collaboration),
//           CSV, JSON, and XML (MIF/MEA)
// ============================================================================

/**
 * IBM Maximo Table Mappings — maps Maximo tables to OptimalAvailability entities
 */
const MAXIMO_TABLE_MAP = {
  // Assets Master
  ASSET: {
    entity: 'equipments',
    fields: {
      ASSETNUM: { target: 'id', label: 'Asset Number' },
      DESCRIPTION: { target: 'name', label: 'Description' },
      ASSETTYPE: { target: 'type', label: 'Asset Type' },
      LOCATION: { target: 'location', label: 'Location' },
      SITEID: { target: 'siteId', label: 'Site ID' },
      STATUS: { target: 'state', label: 'Status' },
      SERIALNUM: { target: 'serialNumber', label: 'Serial Number' },
      MANUFACTURER: { target: 'manufacturer', label: 'Manufacturer' },
      VENDOR: { target: 'vendor', label: 'Vendor' },
      PURCHASEPRICE: { target: 'acquisitionValue', label: 'Purchase Price' },
      INSTALLDATE: { target: 'installDate', label: 'Install Date' },
      PRIORITY: { target: 'priority', label: 'Priority' },
      FAILURECODE: { target: 'failureCode', label: 'Failure Code' },
      GROUPNAME: { target: 'groupName', label: 'Asset Group' }
    }
  },

  // Locations
  LOCATIONS: {
    entity: 'locations',
    fields: {
      LOCATION: { target: 'id', label: 'Location' },
      DESCRIPTION: { target: 'name', label: 'Description' },
      TYPE: { target: 'type', label: 'Location Type' },
      SITEID: { target: 'siteId', label: 'Site ID' },
      PARENT: { target: 'parentLocation', label: 'Parent Location' },
      STATUS: { target: 'state', label: 'Status' },
      GLACCOUNT: { target: 'costCenter', label: 'GL Account' }
    }
  },

  // Work Orders (Maintenance Orders)
  WORKORDER: {
    entity: 'maintenanceorders',
    fields: {
      WONUM: { target: 'id', label: 'Work Order Number' },
      DESCRIPTION: { target: 'description', label: 'Description' },
      WORKTYPE: { target: 'orderType', label: 'Work Type' },
      ASSETNUM: { target: 'equipmentId', label: 'Asset Number' },
      LOCATION: { target: 'locationId', label: 'Location' },
      SCHEDSTART: { target: 'scheduledStart', label: 'Scheduled Start' },
      SCHEDFINISH: { target: 'scheduledEnd', label: 'Scheduled Finish' },
      ACTSTART: { target: 'actualStart', label: 'Actual Start' },
      ACTFINISH: { target: 'actualEnd', label: 'Actual Finish' },
      ACTLABHRS: { target: 'actualWork', label: 'Actual Labor Hours' },
      ESTLABHRS: { target: 'plannedWork', label: 'Estimated Labor Hours' },
      WOPRIORITY: { target: 'priority', label: 'Priority' },
      STATUS: { target: 'status', label: 'Status' },
      TOTALCOST: { target: 'totalCost', label: 'Total Cost' }
    }
  },

  // Job Plans (Task Lists)
  JOBPLAN: {
    entity: 'tasklists',
    fields: {
      JPNUM: { target: 'id', label: 'Job Plan Number' },
      DESCRIPTION: { target: 'description', label: 'Description' },
      PLUSCREVNUM: { target: 'revision', label: 'Revision' },
      SITEID: { target: 'siteId', label: 'Site ID' },
      STATUS: { target: 'status', label: 'Status' },
      TEMPLATEID: { target: 'templateId', label: 'Template ID' }
    }
  },

  // Preventive Maintenance Plans
  PM: {
    entity: 'maintenanceplans',
    fields: {
      PMNUM: { target: 'id', label: 'PM Number' },
      DESCRIPTION: { target: 'description', label: 'Description' },
      FREQUENCY: { target: 'cycleLength', label: 'Frequency' },
      FREQUNIT: { target: 'unit', label: 'Frequency Unit' },
      NEXTDATE: { target: 'nextDue', label: 'Next Due Date' },
      ASSETNUM: { target: 'equipmentId', label: 'Asset Number' },
      LOCATION: { target: 'locationId', label: 'Location' },
      JPNUM: { target: 'jobPlanId', label: 'Job Plan Number' },
      PRIORITY: { target: 'priority', label: 'Priority' }
    }
  },

  // Items (Spare Parts/Inventory)
  ITEM: {
    entity: 'spares',
    fields: {
      ITEMNUM: { target: 'id', label: 'Item Number' },
      DESCRIPTION: { target: 'name', label: 'Description' },
      ORDERUNIT: { target: 'unit', label: 'Order Unit' },
      ISSUEUNIT: { target: 'issueUnit', label: 'Issue Unit' },
      LOTTYPE: { target: 'lotType', label: 'Lot Type' },
      CONDITIONENABLED: { target: 'conditionEnabled', label: 'Condition Enabled' },
      ROTATING: { target: 'rotating', label: 'Rotating' },
      AVGCOST: { target: 'cost', label: 'Average Cost' },
      ORDERQTY: { target: 'orderQuantity', label: 'Order Quantity' }
    }
  },

  // Crafts (Labor Skills)
  CRAFT: {
    entity: 'labor',
    fields: {
      CRAFT: { target: 'id', label: 'Craft Code' },
      DESCRIPTION: { target: 'name', label: 'Description' },
      STANDARDRATE: { target: 'rate', label: 'Standard Rate' },
      SKILLLEVEL: { target: 'skillLevel', label: 'Skill Level' }
    }
  },

  // Commodities
  COMMODITIES: {
    entity: 'commodities',
    fields: {
      COMMODITY: { target: 'id', label: 'Commodity Code' },
      DESCRIPTION: { target: 'name', label: 'Description' },
      COMMODITYGROUP: { target: 'group', label: 'Commodity Group' },
      SERVICETYP: { target: 'serviceType', label: 'Service Type' }
    }
  },

  // Failure Codes
  FAILURECODE: {
    entity: 'failurecodes',
    fields: {
      FAILURECODE: { target: 'id', label: 'Failure Code' },
      DESCRIPTION: { target: 'description', label: 'Description' },
      ORGID: { target: 'orgId', label: 'Organization ID' },
      PARENT: { target: 'parentCode', label: 'Parent Code' }
    }
  }
};

/**
 * Maximo Connection Configuration
 */
const DEFAULT_MAXIMO_CONFIG = {
  connectionType: 'file', // 'file' | 'rest' | 'oslc'
  // REST API connection
  rest: {
    baseUrl: '',
    apiKey: '',
    authType: 'apikey', // 'apikey' | 'basic' | 'oauth2'
    maxauth: ''
  },
  // OSLC connection
  oslc: {
    baseUrl: '',
    leanUrl: '',
    authType: 'basic' // 'basic' | 'oauth2'
  },
  // File-based import/export
  file: {
    format: 'csv', // 'csv' | 'json' | 'xml'
    delimiter: ',',
    encoding: 'utf-8',
    dateFormat: 'YYYY-MM-DD'
  },
  // Sync settings
  sync: {
    autoSync: false,
    interval: 3600, // seconds
    lastSync: null,
    syncDirection: 'bidirectional', // 'import' | 'export' | 'bidirectional'
    conflictResolution: 'maximo-wins' // 'maximo-wins' | 'local-wins' | 'manual'
  },
  // Field mapping overrides
  mappingOverrides: {}
};

/**
 * Maximo Connector Engine
 */
class MaximoConnector {
  /**
   * Create a new Maximo connector instance
   * @param {Object} config - Configuration object
   */
  constructor(config = {}) {
    this.config = { ...DEFAULT_MAXIMO_CONFIG, ...config };
    this.syncLog = [];
    this.fieldMappings = { ...MAXIMO_TABLE_MAP };
  }

  // ========== CONNECTION MANAGEMENT ==========

  /**
   * Test connection to Maximo
   * @returns {Object} Connection test result
   */
  async testConnection() {
    const { connectionType } = this.config;

    if (connectionType === 'file') {
      return {
        success: true,
        message: 'File-based mode — no connection required',
        mode: 'file'
      };
    }

    if (connectionType === 'rest') {
      const { baseUrl, authType } = this.config.rest;
      if (!baseUrl) return { success: false, message: 'REST base URL not configured' };

      // In production, this would call Maximo REST API
      return {
        success: true,
        message: `REST connection configured: ${baseUrl}`,
        mode: 'rest',
        authType,
        note: 'REST API requires valid authentication credentials'
      };
    }

    if (connectionType === 'oslc') {
      const { baseUrl } = this.config.oslc;
      if (!baseUrl) return { success: false, message: 'OSLC base URL not configured' };

      return {
        success: true,
        message: `OSLC connection configured: ${baseUrl}`,
        mode: 'oslc',
        note: 'OSLC supports Open Services for Lifecycle Collaboration'
      };
    }

    return { success: false, message: 'Unknown connection type' };
  }

  /**
   * Get available Maximo tables for the current configuration
   * @returns {Array} Available tables and their field mappings
   */
  getAvailableTables() {
    return Object.entries(MAXIMO_TABLE_MAP).map(([table, config]) => ({
      table,
      entity: config.entity,
      fieldCount: Object.keys(config.fields).length,
      fields: Object.entries(config.fields).map(([maximoField, mapping]) => ({
        maximoField,
        localField: mapping.target,
        label: mapping.label
      }))
    }));
  }

  // ========== IMPORT FROM MAXIMO ==========

  /**
   * Import CSV data exported from Maximo
   * @param {string} csvContent - Raw CSV content
   * @param {string} maximoTable - Maximo table name (ASSET, WORKORDER, etc.)
   * @returns {Object} Imported data mapped to local entities
   */
  importCSV(csvContent, maximoTable) {
    const tableConfig = this.fieldMappings[maximoTable];
    if (!tableConfig) {
      return { success: false, error: `Unknown Maximo table: ${maximoTable}`, records: [] };
    }

    const lines = csvContent.trim().split('\n');
    if (lines.length < 2) {
      return { success: false, error: 'CSV file is empty or has no data rows', records: [] };
    }

    const delimiter = this.config.file.delimiter || ',';
    const headers = this._parseCSVLine(lines[0], delimiter);
    const records = [];
    const warnings = [];

    for (let i = 1; i < lines.length; i++) {
      const values = this._parseCSVLine(lines[i], delimiter);
      if (values.length === 0) continue;

      const record = {};
      let mapped = 0;

      headers.forEach((header, idx) => {
        const cleanHeader = header.trim().replace(/^"|"$/g, '');
        const fieldMapping = tableConfig.fields[cleanHeader];

        if (fieldMapping) {
          record[fieldMapping.target] = this._parseValue(values[idx], cleanHeader);
          mapped++;
        } else {
          // Try fuzzy match
          const fuzzyMatch = this._fuzzyMatchField(cleanHeader, tableConfig.fields);
          if (fuzzyMatch) {
            record[fuzzyMatch.target] = this._parseValue(values[idx], cleanHeader);
            mapped++;
            if (i === 1) {
              warnings.push(`Fuzzy matched: "${cleanHeader}" → ${fuzzyMatch.target}`);
            }
          } else {
            // Store as custom field
            record[`maximo_${cleanHeader}`] = values[idx]?.trim();
          }
        }
      });

      if (mapped > 0 && record.id) {
        records.push(record);
      } else if (mapped > 0) {
        record.id = `MAXIMO-${maximoTable}-${String(i).padStart(4, '0')}`;
        records.push(record);
      }
    }

    this._logSync('import', maximoTable, records.length, 'csv');

    return {
      success: true,
      table: maximoTable,
      entity: tableConfig.entity,
      records,
      totalRows: lines.length - 1,
      mappedRows: records.length,
      warnings
    };
  }

  /**
   * Import from CSV using real parsing
   * @param {string} csvText - Raw CSV text content
   * @returns {Object} Import result with detected table and records
   */
  importFromCSV(csvText) {
    if (!csvText || typeof csvText !== 'string') {
      return { success: false, error: 'Invalid CSV content', records: [] };
    }

    const detectedTable = this._detectTableFromCSVHeaders(csvText.split('\n')[0]);
    if (!detectedTable) {
      return { success: false, error: 'Could not detect Maximo table from CSV headers', records: [] };
    }

    return this.importCSV(csvText, detectedTable);
  }

  /**
   * Static helper to map a single Maximo record using field definitions
   * @param {Object} record - Raw Maximo record
   * @param {Object} tableMap - Table field mapping configuration
   * @returns {Object} Mapped record
   */
  static _mapMaximoRecord(record, tableMap) {
    const mapped = {};
    if (tableMap && tableMap.fields) {
      Object.entries(tableMap.fields).forEach(([maximoField, fieldDef]) => {
        if (record[maximoField] !== undefined) {
          mapped[fieldDef.target] = record[maximoField];
        }
      });
    }
    return Object.keys(mapped).length > 0 ? mapped : record;
  }

  /**
   * Import JSON data (Maximo REST API response format)
   * @param {Object} jsonData - Parsed JSON object
   * @param {string} maximoTable - Target Maximo table
   * @returns {Object} Imported data mapped to local entities
   */
  importJSON(jsonData, maximoTable) {
    const tableConfig = this.fieldMappings[maximoTable];
    if (!tableConfig) {
      return { success: false, error: `Unknown Maximo table: ${maximoTable}`, records: [] };
    }

    // Handle Maximo REST API response wrapper { member: [...] }
    let dataArray = jsonData.member || jsonData.results || jsonData.value || jsonData;

    // Handle OSLC format (rdf:RDF wrapper)
    if (dataArray && typeof dataArray === 'object' && !Array.isArray(dataArray)) {
      dataArray = Object.values(dataArray).filter(item => typeof item === 'object' && item !== null);
    }

    if (!Array.isArray(dataArray)) {
      return { success: false, error: 'Expected array of records', records: [] };
    }

    const records = dataArray.map((item, idx) => {
      const record = {};
      Object.entries(tableConfig.fields).forEach(([maximoField, mapping]) => {
        if (item[maximoField] !== undefined) {
          record[mapping.target] = this._parseValue(item[maximoField], maximoField);
        }
      });
      if (!record.id) {
        record.id = `MAXIMO-${maximoTable}-${String(idx + 1).padStart(4, '0')}`;
      }
      return record;
    });

    this._logSync('import', maximoTable, records.length, 'json');

    return {
      success: true,
      table: maximoTable,
      entity: tableConfig.entity,
      records,
      totalRows: dataArray.length,
      mappedRows: records.length
    };
  }

  /**
   * Import Maximo Integration Framework (MIF) / MEA XML format
   * @param {string} xmlContent - XML content
   * @returns {Object} Imported data
   */
  importXML(xmlContent) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlContent, 'text/xml');

    const parseError = doc.querySelector('parsererror');
    if (parseError) {
      return { success: false, error: 'Invalid XML format', records: [] };
    }

    const results = {
      success: true,
      imports: [],
      totalRecords: 0,
      warnings: []
    };

    // Find all root elements (MEA structure)
    const rootElements = doc.documentElement.children;

    for (const element of rootElements) {
      const elementName = element.tagName.toUpperCase();

      // Map XML element names to Maximo tables
      const tableMapping = this._xmlElementToTable(elementName);
      if (!tableMapping) continue;

      const tableConfig = this.fieldMappings[tableMapping];
      if (!tableConfig) continue;

      const record = {};
      for (const field of element.children) {
        const fieldName = field.tagName.toUpperCase();
        const fieldValue = field.textContent?.trim();

        if (tableConfig.fields[fieldName]) {
          record[tableConfig.fields[fieldName].target] = this._parseValue(fieldValue, fieldName);
        }
      }

      if (Object.keys(record).length > 0) {
        if (!record.id) {
          record.id = `MEA-${tableMapping}-${results.totalRecords + 1}`;
        }
        results.imports.push({
          table: tableMapping,
          entity: tableConfig.entity,
          record
        });
        results.totalRecords++;
      }
    }

    this._logSync('import', 'XML/MEA', results.totalRecords, 'xml');
    return results;
  }

  /**
   * Auto-detect file format and import
   * @param {string} content - File content
   * @param {string} filename - Original filename for format detection
   * @returns {Object} Import results
   */
  autoImport(content, filename) {
    const ext = filename?.split('.').pop()?.toLowerCase();

    // Try XML first
    if (ext === 'xml' || content.trim().startsWith('<')) {
      const result = this.importXML(content);
      if (result.success && result.totalRecords > 0) return result;
    }

    // Try JSON
    if (ext === 'json' || content.trim().startsWith('{') || content.trim().startsWith('[')) {
      try {
        const json = JSON.parse(content);
        // Detect which table based on field names
        const detectedTable = this._detectTableFromFields(
          Array.isArray(json) ? json[0] : json.member?.[0] || json.value?.[0] || json
        );
        if (detectedTable) {
          return this.importJSON(json, detectedTable);
        }
      } catch (e) { /* not valid JSON */ }
    }

    // Try CSV — detect table from headers
    const firstLine = content.split('\n')[0];
    const detectedTable = this._detectTableFromCSVHeaders(firstLine);
    if (detectedTable) {
      return this.importCSV(content, detectedTable);
    }

    // Try all tables
    for (const table of Object.keys(this.fieldMappings)) {
      const result = this.importCSV(content, table);
      if (result.success && result.mappedRows > 0) return result;
    }

    return {
      success: false,
      error: 'Could not detect Maximo data format or table type',
      records: []
    };
  }

  // ========== EXPORT TO MAXIMO ==========

  /**
   * Export local data to Maximo CSV format
   * @param {Array} data - Local entity data
   * @param {string} maximoTable - Target Maximo table
   * @returns {string} CSV content
   */
  exportCSV(data, maximoTable) {
    const tableConfig = this.fieldMappings[maximoTable];
    if (!tableConfig) return '';

    const delimiter = this.config.file.delimiter || ',';
    const reverseMap = {};
    Object.entries(tableConfig.fields).forEach(([maximoField, mapping]) => {
      reverseMap[mapping.target] = maximoField;
    });

    // Header row — Maximo field names
    const maximoFields = Object.keys(tableConfig.fields);
    const header = maximoFields.map(f => `"${f}"`).join(delimiter);

    // Data rows
    const rows = data.map(item => {
      return maximoFields.map(maximoField => {
        const localField = tableConfig.fields[maximoField].target;
        const value = item[localField] ?? '';
        return `"${String(value).replace(/"/g, '""')}"`;
      }).join(delimiter);
    });

    this._logSync('export', maximoTable, data.length, 'csv');
    return [header, ...rows].join('\n');
  }

  /**
   * Export local data to JSON (Maximo REST API compatible format)
   * @param {Array} data - Local entity data
   * @param {string} maximoTable - Target Maximo table
   * @returns {Object} JSON in Maximo REST API response format
   */
  exportJSON(data, maximoTable) {
    const tableConfig = this.fieldMappings[maximoTable];
    if (!tableConfig) return { member: [] };

    const member = data.map(item => {
      const record = {};
      Object.entries(tableConfig.fields).forEach(([maximoField, mapping]) => {
        record[maximoField] = item[mapping.target] ?? '';
      });
      return record;
    });

    this._logSync('export', maximoTable, data.length, 'json');

    return {
      member,
      resourceMetaData: {
        totalCount: data.length,
        pageNum: 1
      }
    };
  }

  /**
   * Export local data to XML (Maximo MIF/MEA format)
   * @param {Array} data - Local entity data
   * @param {string} maximoTable - Target Maximo table
   * @returns {string} XML content in MEA format
   */
  exportXML(data, maximoTable) {
    const tableConfig = this.fieldMappings[maximoTable];
    if (!tableConfig) return '';

    let xml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
    xml += `<MEA>\n`;

    const elementName = maximoTable;

    data.forEach((item, idx) => {
      xml += `  <${elementName}>\n`;
      Object.entries(tableConfig.fields).forEach(([maximoField, mapping]) => {
        const value = item[mapping.target] ?? '';
        xml += `    <${maximoField}>${this._escapeXml(String(value))}</${maximoField}>\n`;
      });
      xml += `  </${elementName}>\n`;
    });

    xml += `</MEA>`;

    this._logSync('export', maximoTable, data.length, 'xml');
    return xml;
  }

  // ========== FIELD MAPPING ==========

  /**
   * Get the field mapping for a specific Maximo table
   * @param {string} maximoTable - Maximo table name
   * @returns {Object|null} Field mapping configuration
   */
  getFieldMapping(maximoTable) {
    const config = this.fieldMappings[maximoTable];
    if (!config) return null;

    return {
      table: maximoTable,
      entity: config.entity,
      fields: Object.entries(config.fields).map(([maximoField, mapping]) => ({
        maximoField,
        localField: mapping.target,
        label: mapping.label,
        override: this.config.mappingOverrides?.[maximoTable]?.[maximoField] || null
      }))
    };
  }

  /**
   * Set a custom field mapping override
   * @param {string} maximoTable - Maximo table name
   * @param {string} maximoField - Maximo field name
   * @param {string} localField - Local field name
   */
  setFieldMappingOverride(maximoTable, maximoField, localField) {
    if (!this.config.mappingOverrides[maximoTable]) {
      this.config.mappingOverrides[maximoTable] = {};
    }
    this.config.mappingOverrides[maximoTable][maximoField] = localField;
  }

  /**
   * Merge imported Maximo data with existing project data
   * @param {Object} projectData - Current project data
   * @param {Array} importedRecords - Records from Maximo import
   * @param {string} entity - Target entity type
   * @param {string} mergeMode - 'replace' | 'merge' | 'append'
   * @returns {Object} Updated project data
   */
  mergeData(projectData, importedRecords, entity, mergeMode = 'merge') {
    const updatedData = { ...projectData };
    const existing = updatedData[entity] || [];

    switch (mergeMode) {
      case 'replace':
        updatedData[entity] = importedRecords;
        break;

      case 'append':
        updatedData[entity] = [...existing, ...importedRecords];
        break;

      case 'merge':
      default: {
        const existingMap = new Map(existing.map(item => [item.id, item]));
        importedRecords.forEach(record => {
          if (existingMap.has(record.id)) {
            // Merge fields — Maximo data overwrites matching fields
            const merged = { ...existingMap.get(record.id), ...record };
            existingMap.set(record.id, merged);
          } else {
            existingMap.set(record.id, record);
          }
        });
        updatedData[entity] = Array.from(existingMap.values());
        break;
      }
    }

    return updatedData;
  }

  // ========== VALIDATION & QUALITY ==========

  /**
   * Validate imported data against Maximo field rules
   * @param {Array} records - Records to validate
   * @param {string} maximoTable - Target Maximo table
   * @returns {Object} Validation results
   */
  validateImport(records, maximoTable) {
    const tableConfig = this.fieldMappings[maximoTable];
    if (!tableConfig) return { valid: false, errors: ['Unknown table'] };

    const errors = [];
    const warnings = [];

    records.forEach((record, idx) => {
      // Check required fields
      if (!record.id && !record.name) {
        errors.push(`Row ${idx + 1}: Missing both ID and Name fields`);
      }

      // Check date formats
      Object.entries(record).forEach(([key, value]) => {
        if (
          key.includes('Date') ||
          key.includes('date') ||
          key.includes('Start') ||
          key.includes('End')
        ) {
          if (value && !/^\d{4}-?\d{2}-?\d{2}/.test(String(value))) {
            warnings.push(
              `Row ${idx + 1}: Field "${key}" has non-standard date format: ${value}`
            );
          }
        }
      });

      // Check numeric fields
      const numericFields = ['actualWork', 'plannedWork', 'cost', 'orderQuantity', 'totalCost'];
      numericFields.forEach(field => {
        if (record[field] && isNaN(parseFloat(record[field]))) {
          warnings.push(`Row ${idx + 1}: Non-numeric ${field} value: ${record[field]}`);
        }
      });
    });

    return {
      valid: errors.length === 0,
      errors,
      warnings,
      recordCount: records.length
    };
  }

  /**
   * Generate a data quality report for imported records
   * @param {Array} records - Records to analyze
   * @param {string} maximoTable - Source Maximo table
   * @returns {Object} Data quality report
   */
  generateDataQualityReport(records, maximoTable) {
    const tableConfig = this.fieldMappings[maximoTable];
    if (!tableConfig) return null;

    const fieldCoverage = {};
    const expectedFields = Object.values(tableConfig.fields).map(f => f.target);

    expectedFields.forEach(field => {
      const filled = records.filter(r => r[field] !== undefined && r[field] !== '').length;
      fieldCoverage[field] = {
        filled,
        total: records.length,
        percentage: records.length > 0 ? ((filled / records.length) * 100).toFixed(1) : 0
      };
    });

    const overallCompleteness =
      expectedFields.reduce((sum, field) => {
        return sum + (parseFloat(fieldCoverage[field].percentage) || 0);
      }, 0) / expectedFields.length;

    return {
      table: maximoTable,
      entity: tableConfig.entity,
      totalRecords: records.length,
      fieldCoverage,
      overallCompleteness: overallCompleteness.toFixed(1),
      duplicateIds: this._findDuplicates(records.map(r => r.id)),
      missingIds: records.filter(r => !r.id).length
    };
  }

  // ========== PERFORMANCE METRICS ==========

  /**
   * Calculate performance KPIs from work order and asset data
   * @param {Array} workOrders - Work order records
   * @param {Array} assets - Asset records
   * @returns {Object} Calculated KPIs (MTBF, MTTR, Availability, OEE)
   */
  calculatePerformanceKPIs(workOrders, assets) {
    if (!Array.isArray(workOrders) || workOrders.length === 0) {
      return {
        mtbf: 0,
        mttr: 0,
        availability: 0,
        oee: 0,
        workOrderCount: 0,
        assetCount: assets?.length || 0
      };
    }

    // Filter for corrective/reactive work orders
    const reactiveWOs = workOrders.filter(wo => wo.orderType?.toUpperCase() === 'CORRECTIVE');

    // MTTR: Mean Time To Repair (average actual work hours)
    const mttrs = reactiveWOs
      .filter(wo => wo.actualWork)
      .map(wo => parseFloat(wo.actualWork) || 0);
    const mttr = mttrs.length > 0 ? mttrs.reduce((a, b) => a + b, 0) / mttrs.length : 0;

    // MTBF: Mean Time Between Failures (time between consecutive reactive WOs per asset)
    let mtbf = 0;
    if (assets && assets.length > 0) {
      const assetFailures = {};
      reactiveWOs.forEach(wo => {
        if (wo.equipmentId) {
          if (!assetFailures[wo.equipmentId]) assetFailures[wo.equipmentId] = [];
          assetFailures[wo.equipmentId].push(new Date(wo.actualStart || wo.scheduledStart));
        }
      });

      const intervals = [];
      Object.values(assetFailures).forEach(dates => {
        const sorted = dates.sort((a, b) => a - b);
        for (let i = 1; i < sorted.length; i++) {
          const diffHours = (sorted[i] - sorted[i - 1]) / (1000 * 60 * 60);
          intervals.push(diffHours);
        }
      });

      mtbf = intervals.length > 0 ? intervals.reduce((a, b) => a + b, 0) / intervals.length : 0;
    }

    // Availability = MTBF / (MTBF + MTTR)
    const availability =
      mtbf + mttr > 0 ? ((mtbf / (mtbf + mttr)) * 100).toFixed(2) : 100;

    // OEE: Operational Equipment Effectiveness = Availability * Performance * Quality
    // (simplified: using availability and reactive WO ratio)
    const preventiveWOs = workOrders.filter(wo =>
      wo.orderType?.toUpperCase().includes('PREVENTIVE')
    );
    const qualityFactor =
      workOrders.length > 0
        ? ((preventiveWOs.length / workOrders.length) * 100).toFixed(2)
        : 100;
    const oee = ((parseFloat(availability) * parseFloat(qualityFactor)) / 100).toFixed(2);

    return {
      mtbf: parseFloat(mtbf.toFixed(2)),
      mttr: parseFloat(mttr.toFixed(2)),
      availability: parseFloat(availability),
      oee: parseFloat(oee),
      workOrderCount: workOrders.length,
      assetCount: assets?.length || 0,
      reactiveWOCount: reactiveWOs.length,
      preventiveWOCount: preventiveWOs.length
    };
  }

  /**
   * Calculate time-to-failure (TTF) analysis from work order history
   * @param {Array} workOrders - Work order records
   * @returns {Object} TTF analysis by asset
   */
  calculateTTF(workOrders) {
    if (!Array.isArray(workOrders) || workOrders.length === 0) {
      return { success: true, analysis: {} };
    }

    const assetTTF = {};

    // Group work orders by equipment
    const byAsset = {};
    workOrders.forEach(wo => {
      if (wo.equipmentId) {
        if (!byAsset[wo.equipmentId]) byAsset[wo.equipmentId] = [];
        byAsset[wo.equipmentId].push(wo);
      }
    });

    // Calculate TTF for each asset
    Object.entries(byAsset).forEach(([assetId, wos]) => {
      const sorted = wos.sort((a, b) => {
        const dateA = new Date(a.actualStart || a.scheduledStart || 0);
        const dateB = new Date(b.actualStart || b.scheduledStart || 0);
        return dateA - dateB;
      });

      const ttfs = [];
      for (let i = 1; i < sorted.length; i++) {
        const dateA = new Date(sorted[i - 1].actualEnd || sorted[i - 1].scheduledEnd || 0);
        const dateB = new Date(sorted[i].actualStart || sorted[i].scheduledStart || 0);
        const hoursToNextFailure = (dateB - dateA) / (1000 * 60 * 60);
        if (hoursToNextFailure > 0) {
          ttfs.push(hoursToNextFailure);
        }
      }

      if (ttfs.length > 0) {
        assetTTF[assetId] = {
          count: ttfs.length,
          averageTTF: parseFloat((ttfs.reduce((a, b) => a + b, 0) / ttfs.length).toFixed(2)),
          minTTF: parseFloat(Math.min(...ttfs).toFixed(2)),
          maxTTF: parseFloat(Math.max(...ttfs).toFixed(2)),
          standardDeviation: parseFloat(
            this._calculateStdDev(ttfs).toFixed(2)
          )
        };
      }
    });

    return {
      success: true,
      analysis: assetTTF,
      assetCount: Object.keys(assetTTF).length
    };
  }

  // ========== SYNC LOG ==========

  /**
   * Get the sync log
   * @returns {Array} Array of sync log entries
   */
  getSyncLog() {
    return [...this.syncLog];
  }

  /**
   * Clear the sync log
   */
  clearSyncLog() {
    this.syncLog = [];
  }

  /**
   * Get the last sync operation
   * @returns {Object|null} Last sync log entry
   */
  getLastSync() {
    return this.syncLog.length > 0 ? this.syncLog[this.syncLog.length - 1] : null;
  }

  // ========== PRIVATE HELPERS ==========

  /**
   * Parse a CSV line handling quoted fields and delimiters
   * @private
   */
  _parseCSVLine(line, delimiter) {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (c === delimiter && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += c;
      }
    }
    result.push(current.trim());
    return result;
  }

  /**
   * Parse field values with type conversion
   * @private
   */
  _parseValue(value, fieldName) {
    if (value === undefined || value === null) return '';
    const str = String(value).trim().replace(/^"|"$/g, '');

    // Parse Maximo date format (YYYY-MM-DD or YYYYMMDD)
    if (
      /^\d{4}-\d{2}-\d{2}/.test(str) ||
      (/^\d{8}$/.test(str) &&
        (fieldName.toUpperCase().includes('DATE') ||
          fieldName.toUpperCase().includes('START') ||
          fieldName.toUpperCase().includes('END')))
    ) {
      if (/^\d{8}$/.test(str)) {
        return `${str.substring(0, 4)}-${str.substring(4, 6)}-${str.substring(6, 8)}`;
      }
      return str;
    }

    // Parse numbers
    if (/^-?\d+\.?\d*$/.test(str) && !fieldName.toUpperCase().includes('NUM')) {
      return str;
    }

    return str;
  }

  /**
   * Fuzzy match field names for flexible field mapping
   * @private
   */
  _fuzzyMatchField(input, fieldMap) {
    const inputUpper = input.toUpperCase().replace(/[_\s-]/g, '');

    for (const [maximoField, mapping] of Object.entries(fieldMap)) {
      const fieldUpper = maximoField.toUpperCase().replace(/[_\s-]/g, '');
      const labelUpper = mapping.label.toUpperCase().replace(/[_\s-]/g, '');

      if (inputUpper === fieldUpper || inputUpper === labelUpper) return mapping;
      if (inputUpper.includes(fieldUpper) || fieldUpper.includes(inputUpper)) return mapping;
      if (inputUpper.includes(labelUpper) || labelUpper.includes(inputUpper)) return mapping;
    }
    return null;
  }

  /**
   * Detect Maximo table from field names in a record
   * @private
   */
  _detectTableFromFields(record) {
    if (!record || typeof record !== 'object') return null;

    const fields = Object.keys(record);
    let bestMatch = null;
    let bestScore = 0;

    for (const [table, config] of Object.entries(this.fieldMappings)) {
      const maximoFields = Object.keys(config.fields);
      const matches = fields.filter(f => maximoFields.includes(f)).length;
      if (matches > bestScore) {
        bestScore = matches;
        bestMatch = table;
      }
    }

    return bestScore >= 2 ? bestMatch : null;
  }

  /**
   * Detect table from CSV header line
   * @private
   */
  _detectTableFromCSVHeaders(headerLine) {
    const headers = headerLine.split(/[,;\t]/).map(h => h.trim().replace(/^"|"$/g, ''));
    return this._detectTableFromFields(
      Object.fromEntries(headers.map(h => [h, true]))
    );
  }

  /**
   * Map XML element names to Maximo tables
   * @private
   */
  _xmlElementToTable(elementName) {
    const map = {
      ASSET: 'ASSET',
      LOCATIONS: 'LOCATIONS',
      WORKORDER: 'WORKORDER',
      JOBPLAN: 'JOBPLAN',
      PM: 'PM',
      ITEM: 'ITEM',
      CRAFT: 'CRAFT',
      COMMODITIES: 'COMMODITIES',
      FAILURECODE: 'FAILURECODE'
    };
    return map[elementName] || null;
  }

  /**
   * Escape XML special characters
   * @private
   */
  _escapeXml(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  /**
   * Log sync operation
   * @private
   */
  _logSync(direction, table, recordCount, format) {
    this.syncLog.push({
      timestamp: new Date().toISOString(),
      direction,
      table,
      recordCount,
      format,
      status: 'success'
    });
  }

  /**
   * Find duplicate values in an array
   * @private
   */
  _findDuplicates(arr) {
    const seen = new Set();
    const dupes = new Set();
    arr.forEach(item => {
      if (item && seen.has(item)) dupes.add(item);
      if (item) seen.add(item);
    });
    return Array.from(dupes);
  }

  /**
   * Calculate standard deviation
   * @private
   */
  _calculateStdDev(values) {
    if (values.length === 0) return 0;
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const variance =
      values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
    return Math.sqrt(variance);
  }
}

/**
 * Maximo Write-Back Handler — converts OptimalAvailability data to Maximo formats
 * Handles bi-directional sync for work orders, job plans, PM schedules, and inventory
 */
class MaximoWriteBack {
  /**
   * Convert OptimalAvailability task to Maximo WORKORDER format
   * @param {Object} taskData - Task object with id, description, type, scheduledStart, equipmentId, etc.
   * @returns {Object} Maximo work order record
   */
  static generateWorkOrder(taskData) {
    return {
      WONUM: taskData.id || `WO-${Date.now()}`,
      DESCRIPTION: (taskData.description || '').substring(0, 100),
      WORKTYPE: taskData.type === 'preventive' ? 'PM' : 'CORRECTIVE',
      ASSETNUM: taskData.equipmentId || '',
      LOCATION: taskData.locationId || '',
      SCHEDSTART: this._formatMaximoDateTime(taskData.scheduledStart),
      SCHEDFINISH: this._formatMaximoDateTime(taskData.scheduledEnd),
      ESTLABHRS: taskData.plannedWork || 0,
      WOPRIORITY: this._mapPriorityToMaximo(taskData.priority),
      STATUS: this._mapStatusToMaximo(taskData.status),
      SITEID: taskData.siteId || 'MAIN'
    };
  }

  /**
   * Convert RCM task recommendations to Maximo job plan format
   * @param {Array} rcmTasks - Array of RCM tasks with description, interval, criticality, etc.
   * @returns {Array} Maximo job plan records
   */
  static generateJobPlan(rcmTasks) {
    return rcmTasks.map((task, idx) => ({
      JPNUM: task.id || `JP-${Date.now()}-${idx}`,
      DESCRIPTION: (task.description || '').substring(0, 100),
      PLUSCREVNUM: '1.0', // Initial revision
      SITEID: task.siteId || 'MAIN',
      STATUS: 'ACTIVE',
      TEMPLATEID: task.templateId || '',
      TASKCOUNT: task.operations ? task.operations.length : 0
    }));
  }

  /**
   * Convert scheduled tasks to Maximo PM (preventive maintenance) records
   * @param {Array} scheduledTasks - Array of scheduled maintenance tasks
   * @returns {Array} Maximo PM records
   */
  static generatePMSchedule(scheduledTasks) {
    return scheduledTasks.map((task) => ({
      PMNUM: task.id || `PM-${Date.now()}`,
      DESCRIPTION: (task.description || '').substring(0, 100),
      FREQUENCY: task.interval || 1,
      FREQUNIT: task.intervalUnit || 'DAYS',
      NEXTDATE: this._formatMaximoDate(task.nextDueDate),
      ASSETNUM: task.equipmentId || '',
      LOCATION: task.locationId || '',
      JPNUM: task.jobPlanId || '',
      PRIORITY: this._mapPriorityToMaximo(task.priority),
      SITEID: task.siteId || 'MAIN'
    }));
  }

  /**
   * Convert spare parts to Maximo ITEM (inventory) format
   * @param {Array} spareData - Array of spare part objects with id, description, cost, unit, etc.
   * @returns {Array} Maximo item records
   */
  static exportInventoryToMaximo(spareData) {
    if (!Array.isArray(spareData)) {
      return [];
    }

    return spareData.map((spare) => ({
      ITEMNUM: spare.id || `ITEM-${Date.now()}`,
      DESCRIPTION: (spare.description || '').substring(0, 100),
      ORDERUNIT: spare.unit || 'EA',
      ISSUEUNIT: spare.issueUnit || spare.unit || 'EA',
      LOTTYPE: spare.lotType || 'REGULAR',
      CONDITIONENABLED: spare.conditionTracked ? true : false,
      ROTATING: spare.rotating ? true : false,
      AVGCOST: spare.cost || spare.unitCost || 0,
      ORDERQTY: spare.orderQuantity || Math.ceil((spare.annualDemand || 0) / 12),
      SITEID: spare.siteId || 'MAIN'
    }));
  }

  /**
   * Create OSLC-compliant sync payload for bi-directional synchronization
   * @param {Array} entities - Array of entities to sync
   * @param {string} direction - 'import' or 'export'
   * @returns {Object} OSLC-compliant sync payload
   */
  static generateOSLCSyncPayload(entities, direction = 'export') {
    return {
      'rdf:RDF': {
        '@xmlns': {
          'rdf': 'http://www.w3.org/1999/02/22-rdf-syntax-ns#',
          'oslc': 'http://open-services.net/ns/core/1.0/',
          'oslc_cm': 'http://open-services.net/ns/cm/1.0/',
          'dcterms': 'http://purl.org/dc/terms/'
        },
        'rdf:Description': {
          '@rdf:about': 'urn:optimal:sync',
          'dcterms:title': 'OptimalAvailability Sync',
          'dcterms:description': `${direction.toUpperCase()} sync from OptimalAvailability to Maximo`,
          'dcterms:issued': new Date().toISOString(),
          'oslc:instanceShape': 'urn:shape:optimal-maximo-sync',
          'oslc:resourceShapes': {
            'rdf:Seq': {
              'rdf:li': entities.map((entity, idx) => ({
                '@rdf:resource': `urn:optimal:entity:${idx}`
              }))
            }
          }
        }
      },
      syncMetadata: {
        timestamp: new Date().toISOString(),
        direction: direction,
        entityCount: entities.length,
        syncId: `OSLC-${Date.now()}`,
        sourceSystem: 'OptimalAvailability',
        targetSystem: 'Maximo',
        protocol: 'OSLC/1.0',
        format: 'RDF/XML'
      },
      data: entities
    };
  }

  // ========== PRIVATE HELPERS ==========

  /**
   * Format date-time to Maximo format (YYYY-MM-DD'T'HH:MM:SS)
   * @private
   */
  static _formatMaximoDateTime(dateValue) {
    if (!dateValue) return '';
    const d = new Date(dateValue);
    if (isNaN(d)) return '';
    return d.toISOString().replace('Z', '');
  }

  /**
   * Format date to Maximo format (YYYY-MM-DD)
   * @private
   */
  static _formatMaximoDate(dateValue) {
    if (!dateValue) return '';
    const d = new Date(dateValue);
    if (isNaN(d)) return '';
    return d.toISOString().split('T')[0];
  }

  /**
   * Map priority value to Maximo priority code
   * @private
   */
  static _mapPriorityToMaximo(priority) {
    const mapping = {
      'critical': 1, 'high': 2, 'medium': 3, 'low': 4,
      1: 1, 2: 2, 3: 3, 4: 4
    };
    return mapping[priority] || 3;
  }

  /**
   * Map task status to Maximo work order status
   * @private
   */
  static _mapStatusToMaximo(status) {
    const mapping = {
      'draft': 'DRAFT',
      'scheduled': 'SCHEDULED',
      'in_progress': 'INPROG',
      'completed': 'COMPLETE',
      'closed': 'CLOSED',
      'cancelled': 'CANCELLED'
    };
    return mapping[status?.toLowerCase()] || 'DRAFT';
  }
}

/**
 * Maximo Live Connector — REST/OSLC API connection for real-time synchronization
 * Connects to IBM Maximo Asset Management via OSLC or REST API
 */
class MaximoLiveConnector {
  constructor(config) {
    this.baseUrl = config.baseUrl || '';
    this.apiKey = config.apiKey || null;
    this.credentials = config.credentials || {};
    this.connected = false;
  }

  async testConnection() {
    try {
      const response = await fetch(`${this.baseUrl}os/mxasset?oslc.pageSize=1`, {
        headers: this._getHeaders(),
        signal: AbortSignal.timeout(10000)
      });
      this.connected = response.ok;
      return { success: response.ok, status: response.status, message: response.ok ? 'Connected to Maximo' : `HTTP ${response.status}` };
    } catch (err) {
      return { success: false, status: 0, message: err.message };
    }
  }

  _getHeaders() {
    const headers = { 'Accept': 'application/json' };
    if (this.apiKey) headers['apikey'] = this.apiKey;
    else if (this.credentials && this.credentials.username) headers['Authorization'] = 'Basic ' + btoa(`${this.credentials.username}:${this.credentials.password}`);
    return headers;
  }

  async fetchAssets(filters = {}) {
    const params = new URLSearchParams({ 'oslc.pageSize': '100' });
    if (filters.site) params.append('oslc.where', `siteid="${filters.site}"`);
    const response = await fetch(`${this.baseUrl}os/mxasset?${params}`, { headers: this._getHeaders() });
    if (!response.ok) throw new Error(`Maximo API error: ${response.status}`);
    const data = await response.json();
    return (data['rdfs:member'] || data.member || []).map(item => MaximoConnector._mapMaximoRecord(item, MAXIMO_TABLE_MAP.ASSET));
  }

  async fetchWorkOrders(filters = {}) {
    const params = new URLSearchParams({ 'oslc.pageSize': '100' });
    if (filters.status) params.append('oslc.where', `status="${filters.status}"`);
    const response = await fetch(`${this.baseUrl}os/mxwo?${params}`, { headers: this._getHeaders() });
    if (!response.ok) throw new Error(`Maximo API error: ${response.status}`);
    const data = await response.json();
    return (data['rdfs:member'] || data.member || []).map(item => MaximoConnector._mapMaximoRecord(item, MAXIMO_TABLE_MAP.WORKORDER));
  }

  async pushWorkOrder(orderData) {
    const response = await fetch(`${this.baseUrl}os/mxwo`, {
      method: 'POST',
      headers: { ...this._getHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(MaximoWriteBack.generateWorkOrder(orderData))
    });
    if (!response.ok) throw new Error(`Maximo POST error: ${response.status}`);
    return await response.json();
  }
}

// ========== STATIC REFERENCES ==========

/**
 * Static reference to Maximo table map
 */
MaximoConnector.TABLE_MAP = MAXIMO_TABLE_MAP;

/**
 * Static reference to default configuration
 */
MaximoConnector.DEFAULT_CONFIG = DEFAULT_MAXIMO_CONFIG;

export default MaximoConnector;
export { MaximoWriteBack, MaximoLiveConnector };
